import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Drawer,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Autorenew as AutorenewIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { toast } from 'react-toastify';
import userService from "../services/userService";
import tenantService from "../services/tenantService";
import tenantUsersService from "../services/tenantUsersService";
import Pagination from "../components/Pagination";
import { darkColors, lightColors } from "../theme";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { createCommonStyles } from "../theme/styles";
import { colors, getStatusColors, getTableRowBackground, getTableHeaderStyles, getActualTableRowBackground } from '../theme/colors';
import { getUserTypeFromConnections } from "../utils/userHelper";

const Users = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const location = useLocation();
  const theme = useTheme();
  const isEmployeesPage = location.pathname === "/employees";
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [showSuperAdmins, setShowSuperAdmins] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterPhone, setFilterPhone] = useState("");
  // refs to hold latest filter values so fetchUsers can read them without
  // being recreated on every keystroke (avoids lint dependency issues)
  const filterNameRef = useRef("");
  const filterEmailRef = useRef("");
  const filterPhoneRef = useRef("");
  const [currentTenant, setCurrentTenant] = useState(null); // Podaci o trenutnom tenant-u
  const [submitting, setSubmitting] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    filterName: "",
    filterEmail: "",
    filterPhone: "",
  });
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    userType: "",
    tenantId: "",
    permissions: {
      canManageUsers: false,
      canManageServices: false,
      canViewReports: false,
    },
    workingHours: {
      startTime: "",
      endTime: ""
    },
    shift: "",
    isActive: true,
  dontRequireConfirmation: false,
  });

  // Helper funkcija za dobijanje tenantId iz tenantConnections
  const getCurrentTenantId = useCallback(() => {
    // Ako postoji tenantId direktno na user objektu (stari način)
    if (currentUser?.tenantId) {
      return currentUser.tenantId?._id || currentUser.tenantId;
    }
    
    // Ako postoji tenantConnections, uzmi tenantId iz prve konekcije
    if (currentUser?.tenantConnections && currentUser.tenantConnections.length > 0) {
      const firstConnection = currentUser.tenantConnections[0];
      return firstConnection?.tenantId?._id || firstConnection?.tenantId;
    }
    
    return null;
  }, [currentUser]);

  // Helper funkcija za čitanje userType iz tenantConnections na osnovu selectedTenantId
  const getUserTypeFromCurrentTenant = useCallback(() => {
    if (!currentUser?.tenantConnections || currentUser.tenantConnections.length === 0) {
      return currentUser?.userType;
    }
    
    // Ako postoji selectedTenantId, traži taj connection
    if (selectedTenantId) {
      const connection = currentUser.tenantConnections.find(
        tc => {
          const tcTenantId = tc.tenantId?._id || tc.tenantId;
          return tcTenantId === selectedTenantId;
        }
      );
      
      if (connection?.userType) {
        return connection.userType;
      }
    }
    
    // Falls back na prvi connection ili user.userType
    const firstConnection = currentUser.tenantConnections[0];
    return firstConnection?.userType || currentUser?.userType;
  }, [currentUser, selectedTenantId]);

  const fetchTenants = useCallback(async () => {
    try {
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const canManageUsers = currentUser?.permissions?.canManageUsers;
      const tenantId = getCurrentTenantId();

      if (isSuperAdmin) {
        const response = await tenantService.getAllTenants();
        setTenants(response.tenants || response);
      } else if ((isTenantAdmin || canManageUsers) && tenantId) {
        // Tenant admin ili employee sa canManageUsers dozvolom
        const t = currentUser.tenantId || currentUser.tenantConnections?.[0]?.tenantId;
        setTenants([
          typeof t === "object" ? t : { _id: t, name: "Your tenant" },
        ]);
      } else {
        setTenants([]);
      }
    } catch (err) {
      console.error("Error fetching tenants:", err);
    }
  }, [currentUser, getCurrentTenantId, getUserTypeFromCurrentTenant]);

  // Centralizovana funkcija za učitavanje korisnika
  // Accept optional filters so we only apply them when Search is clicked
  const fetchUsers = useCallback(async () => {
    try {
      const fName = filterNameRef.current;
      const fEmail = filterEmailRef.current;
      const fPhone = filterPhoneRef.current;
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const tenantId = getCurrentTenantId();

      let response;
      if (isSuperAdmin) {
        if (showSuperAdmins) {
          // Prikaži sve super_admin korisnike (filter client-side)
          response = await userService.getUsersByType("super_admin", page + 1, rowsPerPage);
          const superAdminUsers = response.users || response || [];
          // Apply client-side filters for name/email/phone only if Search was clicked (filters passed in)
          const filtered = Array.isArray(superAdminUsers)
            ? superAdminUsers.filter(u => {
                const fullName = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
                const email = (u.email || "").toLowerCase();
                const phone = (u.phone || "").toLowerCase();

                if (fName && !fullName.includes(fName.toLowerCase())) return false;
                if (fEmail && !email.includes(fEmail.toLowerCase())) return false;
                if (fPhone && !phone.includes(fPhone.toLowerCase())) return false;
                return true;
              })
            : [];

          setUsers(filtered);
          setTotalCount(response.total || filtered.length || 0);
        } else if (selectedTenantId) {
          // Prikaži korisnike za odabranog tenanta
          response = await tenantUsersService.getTenantUsers({ tenantId: selectedTenantId, isEmployeesPage:isEmployeesPage, page: page + 1, limit: rowsPerPage, name: fName, email: fEmail, phone: fPhone });
          const allUsers = response.users || [];
          const totalCount = response.total || 0;
          // Filtrirati u zavisnosti od rute - koristi helper funkciju za proveru userType iz tenantConnections
          // const filteredUsers = isEmployeesPage
          //   ? Array.isArray(allUsers) ? allUsers.filter(u => {
          //       const userActualType = getUserTypeFromConnections(u, selectedTenantId);
          //       const finalUserType = userActualType || u.userType;
          //       return finalUserType === "employee" || finalUserType === "tenant_admin";
          //     }) : []
          //   : Array.isArray(allUsers) ? allUsers.filter(u => {
          //       const userActualType = getUserTypeFromConnections(u, selectedTenantId);
          //       const finalUserType = userActualType || u.userType;
          //       return !finalUserType || finalUserType === "customer" || finalUserType === "";
          //     }) : [];
          setUsers(allUsers);
          setTotalCount(totalCount);
        } else {
          setUsers([]);
          setTotalCount(0);
          return;
        }
      } else if ((isTenantAdmin || currentUser?.permissions?.canManageUsers) && tenantId) {
        // Tenant admin ili employee sa canManageUsers dozvolom
        response = await tenantUsersService.getTenantUsers({ tenantId, isEmployeesPage:isEmployeesPage, page: page + 1, limit: rowsPerPage, name: fName, email: fEmail, phone: fPhone });
        console.log('response', response);
        const allUsers = response.users || [];
        const totalCount = response.total || 0;
        // Filtrirati u zavisnosti od rute - koristi helper funkciju za proveru userType iz tenantConnections
        // const filteredUsers = isEmployeesPage
        //   ? Array.isArray(allUsers) ? allUsers.filter(u => {
        //       const userActualType = getUserTypeFromConnections(u, tenantId);
        //       const finalUserType = userActualType || u.userType;
        //       return finalUserType === "employee" || finalUserType === "tenant_admin";
        //     }) : []
        //   : Array.isArray(allUsers) ? allUsers.filter(u => {
        //       const userActualType = getUserTypeFromConnections(u, tenantId);
        //       const finalUserType = userActualType || u.userType;
        //       return !finalUserType || finalUserType === "customer" || finalUserType === "";
        //     }) : [];
        setUsers(allUsers);
        setTotalCount(totalCount);
      } else {
        setUsers([]);
        setTotalCount(0);
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Greška pri učitavanju korisnika');
    }
  }, [showSuperAdmins, selectedTenantId, page, rowsPerPage, getCurrentTenantId, getUserTypeFromCurrentTenant, isEmployeesPage, currentUser]);
  console.log('total count', totalCount);
  useEffect(() => {
    const initializeData = async () => {
      await fetchTenants();
      // Ne pozivaj fetchUsers ovde jer će se pozvati u drugom useEffect-u
    };
    initializeData();
  }, [fetchTenants]);

  // keep refs in sync with state so fetchUsers can use refs when called
  useEffect(() => {
    filterNameRef.current = filterName;
  }, [filterName]);

  useEffect(() => {
    filterEmailRef.current = filterEmail;
  }, [filterEmail]);

  useEffect(() => {
    filterPhoneRef.current = filterPhone;
  }, [filterPhone]);

  // Funkcija za izračunavanje najranijeg i najkasnijeg radnog vremena iz tenant-a
  const getDefaultWorkingHours = useCallback(() => {
    if (!currentTenant?.workingHours) {
      return { startTime: "09:00", endTime: "17:00" };
    }

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    let earliestTime = "23:59";
    let latestTime = "00:00";

    days.forEach(day => {
      const dayHours = currentTenant.workingHours[day];
      if (dayHours && dayHours.isOpen) {
        if (dayHours.open && dayHours.open < earliestTime) {
          earliestTime = dayHours.open;
        }
        if (dayHours.close && dayHours.close > latestTime) {
          latestTime = dayHours.close;
        }
      }
    });

    return { startTime: earliestTime, endTime: latestTime };
  }, [currentTenant]);

  const handleOpenDialog = useCallback((user = null) => {
         if (user) {
       setEditingUser(user);
       setFormData({
         firstName: user.firstName || "",
         lastName: user.lastName || "",
         email: user.email || "",
         phone: user.phone || "",
         password: "",
         userType: user.userType || "",
         tenantId: user.userType === "super_admin" ? "" : (user.tenantId?._id || user.tenantId || ""),
         permissions: {
           canManageUsers: user.permissions?.canManageUsers || false,
           canManageServices: user.permissions?.canManageServices || false,
           canViewReports: user.permissions?.canViewReports || false,
         },
         workingHours: {
           startTime: user.workingHours?.startTime || "",
           endTime: user.workingHours?.endTime || ""
         },
         shift: user.shift || "",
         isActive: user.isActive !== undefined ? user.isActive : true,
         dontRequireConfirmation: user.dontRequireConfirmation || false,
       });
    } else {
      setEditingUser(null);
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const tenantId = getCurrentTenantId();
      const defaultHours = getDefaultWorkingHours();
      
      // Odredi default userType
      let defaultUserType = "";
      if (isSuperAdmin && showSuperAdmins) {
        defaultUserType = "super_admin";
      } else if (isEmployeesPage) {
        defaultUserType = "employee"; // Za /employees stranicu uvek employee
      } else {
        defaultUserType = "customer"; // Za /users stranicu uvek customer
      }
      
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        userType: defaultUserType,
        tenantId: isSuperAdmin ? (showSuperAdmins ? "" : selectedTenantId) : tenantId,
        permissions: {
          canManageUsers: false,
          canManageServices: false,
          canViewReports: false,
        },
        workingHours: {
          startTime: defaultHours.startTime,
          endTime: defaultHours.endTime
        },
        shift: "",
        isActive: true,
  dontRequireConfirmation: false,
      });
    }
    setOpenDialog(true);
  }, [showSuperAdmins, selectedTenantId, getDefaultWorkingHours, getUserTypeFromCurrentTenant, getCurrentTenantId, isEmployeesPage]);

  // Event listener za otvaranje dijaloga iz header-a
  useEffect(() => {
    const handleOpenUserDialog = () => {
      handleOpenDialog();
    };

    const handleOpenEmployeeDialog = () => {
      handleOpenDialog();
    };

    window.addEventListener('openUserDialog', handleOpenUserDialog);
    window.addEventListener('openEmployeeDialog', handleOpenEmployeeDialog);
    return () => {
      window.removeEventListener('openUserDialog', handleOpenUserDialog);
      window.removeEventListener('openEmployeeDialog', handleOpenEmployeeDialog);
    };
  }, [handleOpenDialog]);

  // Učitaj korisnike kada se promene zavisnosti
  useEffect(() => {
    // Initial load: fetch unfiltered list. Filters are applied only when user clicks "Pretraži".
    fetchUsers();
  }, [fetchUsers]);

  // Učitaj tenant podatke kada se promeni selectedTenantId ili currentUser
  useEffect(() => {
    const loadTenantData = async () => {
      const tenantId = selectedTenantId || currentUser?.tenantId?._id || currentUser?.tenantId;
      
      if (tenantId) {
        try {
          const tenant = await tenantService.getTenantById(tenantId);
          setCurrentTenant(tenant);
        } catch (err) {
          console.error("Error loading tenant:", err);
          setCurrentTenant(null);
        }
        } else {
        setCurrentTenant(null);
      }
    };

    loadTenantData();
  }, [selectedTenantId, currentUser]);

  // Automatski popuni radno vreme kada se promeni userType na employee ili tenant_admin
  useEffect(() => {
    if ((formData.userType === "employee" || formData.userType === "tenant_admin") && 
        !formData.workingHours.startTime && 
        !editingUser) {
      const defaultHours = getDefaultWorkingHours();
      setFormData(prev => ({
        ...prev,
        workingHours: defaultHours
      }));
    }
  }, [formData.userType, formData.workingHours.startTime, editingUser, getDefaultWorkingHours]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    const actualUserType = getUserTypeFromCurrentTenant();
    const isSuperAdmin = actualUserType === "super_admin";
    const tenantId = getCurrentTenantId();
    
    // Odredi default userType za zatvaranje (neće se koristiti jer se resetuje pri otvaranju)
    let defaultUserType = "";
    if (isSuperAdmin && showSuperAdmins) {
      defaultUserType = "super_admin";
    } else if (isEmployeesPage) {
      defaultUserType = "employee";
    } else {
      defaultUserType = "customer";
    }
    
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      userType: defaultUserType,
      tenantId: isSuperAdmin ? (showSuperAdmins ? "" : selectedTenantId) : tenantId,
      permissions: {
        canManageUsers: false,
        canManageServices: false,
        canViewReports: false,
      },
      workingHours: {
        startTime: "",
        endTime: ""
      },
      shift: "",
      isActive: true,
  dontRequireConfirmation: false,
    });
  };

  const handleSubmit = async () => {
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const canManageUsers = currentUser?.permissions?.canManageUsers;
      const tenantId = getCurrentTenantId();

      // Proveri dozvolu: super_admin, tenant_admin ili employee sa canManageUsers
      if (!isSuperAdmin && !isTenantAdmin && !canManageUsers) {
        toast.error("Nemate dozvolu za kreiranje/uređivanje korisnika");
        return;
      }

             if (isSuperAdmin && !selectedTenantId && !showSuperAdmins) {
         toast.error("Morate izabrati klijenta iz dropdown-a ili uključiti prikaz super_admin korisnika");
         return;
       }

      if (!formData.userType || formData.userType === "") {
        toast.error("Morate izabrati tip korisnika");
         return;
       }

             // Build payload without mutating state
       const dataToSend = {
         ...formData,
         tenantId: isSuperAdmin ? (showSuperAdmins ? "" : selectedTenantId) : tenantId,
       };

      if (editingUser && !dataToSend.password) {
        delete dataToSend.password;
      }

      if (editingUser) {
        // Ako se edituje super_admin korisnik, ne treba tenantId
        if (dataToSend.userType === "super_admin") {
          dataToSend.tenantId = "";
        }
        await userService.updateUser(editingUser._id, dataToSend);
      } else {
        if (!dataToSend.password) {
          toast.error("Password je obavezan za novog korisnika");
          return;
        }
        
        // Ako se kreira super_admin korisnik, ne treba tenantId
        if (dataToSend.userType === "super_admin") {
          dataToSend.tenantId = "";
        }
        
        await userService.createUser(dataToSend);
      }
      toast.success(editingUser ? 'Korisnik je uspešno ažuriran!' : 'Korisnik je uspešno kreiran!');
      handleCloseDialog();
      // Ponovno učitaj podatke (uses refs to pick up active filters)
      await fetchUsers();
        } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Greška pri čuvanju korisnika');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      window.confirm("Da li ste sigurni da želite da obrišete ovog korisnika?")
    ) {
      try {
        const actualUserType = getUserTypeFromCurrentTenant();
        const isSuperAdmin = actualUserType === "super_admin";
        const isTenantAdmin = actualUserType === "tenant_admin";
        const canManageUsers = currentUser?.permissions?.canManageUsers;
        const tenantId = getCurrentTenantId();

        // Proveri dozvolu: super_admin, tenant_admin ili employee sa canManageUsers
        if (!isSuperAdmin && !isTenantAdmin && !canManageUsers) {
          toast.error("Nemate dozvolu za brisanje korisnika");
          return;
        }

        // if (!isSuperAdmin && tenantId) {
        //   const userToDelete = users.find((u) => u._id === id);
        //   if (
        //     userToDelete?.tenantId?._id !== tenantId &&
        //     userToDelete?.tenantId !== tenantId
        //   ) {
        //     setError("Možete obrisati samo korisnike iz vašeg tenant-a");
        //     return;
        //   }
        // }

        await tenantUsersService.deleteTenantUser(id, tenantId || selectedTenantId);
        toast.success("Korisnik je uspešno obrisan!");
        // Ponovno učitaj podatke (uses refs to pick up active filters)
        await fetchUsers();
          } catch (err) {
        toast.error(err.response?.data?.message || err.message || 'Greška pri brisanju korisnika');
      }
    }
  };
  console.log('formdata', formData);
  const handleToggleDontRequireConfirmation = async (userId, currentValue) => {
    try {
      const actualUserType = getUserTypeFromCurrentTenant();
      const isSuperAdmin = actualUserType === "super_admin";
      const isTenantAdmin = actualUserType === "tenant_admin";
      const tenantId = getCurrentTenantId();

      // Proveri dozvolu
      if (!isSuperAdmin && !isTenantAdmin) {
        toast.error("Nemate dozvolu za uređivanje korisnika");
        return;
      }

      // pozovi tenantUsersService koji očekuje (userId, tenantId, dontRequireConfirmation)
      await tenantUsersService.updateUser(userId, tenantId || selectedTenantId, !currentValue);
      toast.success("Postavka je uspešno ažurirana!");
      
      // Ažuriraj lokalni state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user._id === userId 
            ? { ...user, dontRequireConfirmation: !currentValue }
            : user
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Greška pri ažuriranju postavke');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchClick = () => {
    setPage(0);
    // Call fetchUsers without args; it will read current values from refs
    fetchUsers();
  };

  const handleClearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterPhone("");
    setPage(0);
    // Clear refs as well and fetch unfiltered list
    filterNameRef.current = "";
    filterEmailRef.current = "";
    filterPhoneRef.current = "";
    fetchUsers();
  };

  // Mobilni filter funkcije
  const handleOpenMobileFilter = () => {
    setTempFilters({
      filterName,
      filterEmail,
      filterPhone,
    });
    setMobileFilterOpen(true);
  };

  const handleCloseMobileFilter = () => {
    setMobileFilterOpen(false);
  };

  const handleApplyMobileFilters = () => {
    setFilterName(tempFilters.filterName);
    setFilterEmail(tempFilters.filterEmail);
    setFilterPhone(tempFilters.filterPhone);
    setPage(0);
    filterNameRef.current = tempFilters.filterName;
    filterEmailRef.current = tempFilters.filterEmail;
    filterPhoneRef.current = tempFilters.filterPhone;
    fetchUsers();
    setMobileFilterOpen(false);
  };

  const handleResetMobileFilters = () => {
    setTempFilters({
      filterName: "",
      filterEmail: "",
      filterPhone: "",
    });
  };

  const handleRemoveActiveFilter = (filterType) => {
    if (filterType === 'name') {
      setFilterName("");
      filterNameRef.current = "";
    } else if (filterType === 'email') {
      setFilterEmail("");
      filterEmailRef.current = "";
    } else if (filterType === 'phone') {
      setFilterPhone("");
      filterPhoneRef.current = "";
    }
    setPage(0);
    fetchUsers();
  };

  // U zavisnosti od rute, prikazujemo različite tipove korisnika
  const userTypes = showSuperAdmins 
      ? ["super_admin"]
    : isEmployeesPage
      ? ["employee", "tenant_admin"] // Na /employees stranici employee i tenant_admin
      : ["customer"]; // Na /users stranici samo customere


  return (
    <div>
      {/* Opcije za super admin - na /users i /employees stranicama */}
       {currentUser?.userType === "super_admin" && (
         <div>
           {/* Switch za super_admin korisnike - samo na /users stranici */}
           {!isEmployeesPage && (
           <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
             <FormControlLabel
               control={
                 <Switch
                   checked={showSuperAdmins}
                   onChange={(e) => {
                     setShowSuperAdmins(e.target.checked);
                     if (e.target.checked) {
                       setSelectedTenantId("");
                     }
                   }}
                   sx={{
                     '& .MuiSwitch-switchBase.Mui-checked': {
                       color: customColors.primary.main,
                     },
                     '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                       backgroundColor: customColors.primary.main,
                     },
                   }}
                 />
               }
               label="Prikaži sve super_admin korisnike"
               sx={commonStyles.textPrimary}
             />
           </Box>
           )}
           
           {(isEmployeesPage || !showSuperAdmins) && (
             <>
               <FormControl fullWidth sx={{ mb: 2 }}>
                 <InputLabel sx={commonStyles.textSecondary}>Izaberite klijenta</InputLabel>
                 <Select
                   value={selectedTenantId}
                   label="Izaberite klijenta"
                   onChange={(e) => setSelectedTenantId(e.target.value)}
                   sx={commonStyles.selectField}
                   MenuProps={{
                     PaperProps: {
                       sx: commonStyles.selectMenu
                     }
                   }}
                 >
                   <MenuItem value="">
                     <em>Izaberite klijenta...</em>
                   </MenuItem>
                   {tenants.map((tenant) => (
                     <MenuItem key={tenant._id} value={tenant._id}>
                       {tenant.name}
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
               {!selectedTenantId && (
                 <Alert severity="info" sx={commonStyles.alert("info")}>
                   Molimo izaberite klijenta da biste videli {isEmployeesPage ? 'zaposlene' : 'korisnike'}.
                 </Alert>
               )}
             </>
           )}
           
           {showSuperAdmins && (
             <Alert severity="info" sx={commonStyles.alert("info")}>
               Prikazani su svi super_admin korisnici (globalni administratori).
             </Alert>
           )}
         </div>
      )}

      <div>
        {/* <Box p={2} sx={{ 
          background: customColors.background.glassStrong,
          borderBottom: `1px solid ${customColors.border.light}`
        }}>
          <Typography variant="h6" gutterBottom sx={commonStyles.textPrimary}>
            Lista korisnika
          </Typography>
          <Typography variant="body2" sx={commonStyles.textSecondary}>
            Ukupno korisnika: {users.length}
          </Typography>
        </Box>
         */}
        {/* Responsive prikaz - tabela za desktop, kartice za mobilne */}
        {isMobile ? (
          // Mobilni prikaz - kartice (dodati filtere za mobilni prikaz)
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Mobilni filteri */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  onClick={handleOpenMobileFilter}
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  sx={{ ...commonStyles.cancelButton, flex: 1 }}
                >
                  Filteri
                </Button>
              </Box>
              {/* Aktivni filteri kao chipovi */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {filterName && (
                  <Chip
                    label={`Ime: ${filterName}`}
                    onDelete={() => handleRemoveActiveFilter('name')}
                    deleteIcon={<CloseIcon />}
                    sx={{
                      backgroundColor: "transparent",
                      color: commonStyles.textPrimary.color,
                      background: getTableHeaderStyles(isDarkMode).cellBackground,
                      border: "none",
                      "& .MuiChip-deleteIcon": {
                        color: commonStyles.textSecondary.color,
                      },
                      "&:hover": {
                        backgroundColor: colors.ui.background.dark,
                      },
                    }}
                  />
                )}
                {filterEmail && (
                  <Chip
                    label={`Email: ${filterEmail}`}
                    onDelete={() => handleRemoveActiveFilter('email')}
                    deleteIcon={<CloseIcon />}
                    sx={{
                      backgroundColor: "transparent",
                      color: commonStyles.textPrimary.color,
                      background: getTableHeaderStyles(isDarkMode).cellBackground,
                      border: "none",
                      "& .MuiChip-deleteIcon": {
                        color: commonStyles.textSecondary.color,
                      },
                      "&:hover": {
                        backgroundColor: colors.ui.background.dark,
                      },
                    }}
                  />
                )}
                {filterPhone && (
                  <Chip
                    label={`Telefon: ${filterPhone}`}
                    onDelete={() => handleRemoveActiveFilter('phone')}
                    deleteIcon={<CloseIcon />}
                    sx={{
                      backgroundColor: "transparent",
                      color: commonStyles.textPrimary.color,
                      background: getTableHeaderStyles(isDarkMode).cellBackground,
                      border: "none",
                      "& .MuiChip-deleteIcon": {
                        color: commonStyles.textSecondary.color,
                      },
                      "&:hover": {
                        backgroundColor: colors.ui.background.dark,
                      },
                    }}
                  />
                )}
              </Box>
            </Box>

            {users.map((user, index) => (
              <Card key={user._id} sx={{
                ...commonStyles.paper,
                backgroundColor: getTableRowBackground(index, isDarkMode),
                border: `1px solid ${customColors.border.main}`,
                borderRadius: 2,
                mb: 1.5
              }}>
                <CardContent sx={{ p: 1.5 }}>
                  {/* Header sa imenom i akcijama */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    justifyContent: 'space-between',
                    mb: 1.5
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <PersonIcon sx={{ mr: 1, color: customColors.primary.main, fontSize: 20 }} />
                      <Typography variant="h6" sx={{ 
                        ...commonStyles.textPrimary,
                        fontSize: '1.1rem',
                        fontWeight: 600
                      }}>
                        {`${user.firstName} ${user.lastName}`}
                      </Typography>
                    </Box>
                    
                    {/* Akcije */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(user)}
                        sx={commonStyles.iconButton(customColors.primary.main)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(user._id)}
                        sx={commonStyles.iconButton(colors.actions.delete)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Informacije u grid layout */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    mb: 1
                  }}>
                    {/* Email */}
                    <Box>
                      <Typography variant="caption" sx={{ 
                        ...commonStyles.textSecondary,
                        fontSize: '0.75rem',
                        display: 'block',
                        mb: 0.5
                      }}>
                        Email:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        ...commonStyles.textPrimary,
                        fontSize: '0.85rem'
                      }}>
                        {user.email}
                      </Typography>
                    </Box>

                    {/* Telefon */}
                    <Box>
                      <Typography variant="caption" sx={{ 
                        ...commonStyles.textSecondary,
                        fontSize: '0.75rem',
                        display: 'block',
                        mb: 0.5
                      }}>
                        Telefon:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        ...commonStyles.textPrimary,
                        fontSize: '0.85rem'
                      }}>
                        {user.phone || 'N/A'}
                      </Typography>
                    </Box>

                    {/* Status */}
                    <Box>
                      <Typography variant="caption" sx={{ 
                        ...commonStyles.textSecondary,
                        fontSize: '0.75rem',
                        display: 'block',
                        mb: 0.5
                      }}>
                        Status:
                      </Typography>
                      <Chip
                        label={user.isActive ? 'Aktivan' : 'Neaktivan'}
                        size="small"
                        color={user.isActive ? 'success' : 'default'}
                        sx={{
                          background: getStatusColors(user.isActive).background,
                          border: getStatusColors(user.isActive).border,
                          color: getStatusColors(user.isActive).light,
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                    </Box>

                    {/* Bez potvrde termina - samo ako tenant ima partiallyRequireConfirmation */}
                    {currentTenant?.settings?.partiallyRequireConfirmation && (
                      <Box>
                        <Typography variant="caption" sx={{ 
                          ...commonStyles.textSecondary,
                          fontSize: '0.75rem',
                          display: 'block',
                          mb: 0.5
                        }}>
                          Ne zahtevaj potvrdu termina:
                        </Typography>
                        <Chip
                          label={user.dontRequireConfirmation ? 'Da' : 'Ne'}
                          size="small"
                          color={user.dontRequireConfirmation ? 'warning' : 'success'}
                          sx={{
                            background: user.dontRequireConfirmation ? 'rgba(251, 146, 60, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                            border: user.dontRequireConfirmation ? '1px solid rgba(251, 146, 60, 0.5)' : '1px solid rgba(34, 197, 94, 0.5)',
                            color: user.dontRequireConfirmation ? '#fbbf24' : '#bbf7d0',
                            fontSize: '0.7rem',
                            height: 20
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Footer sa klijentom */}
                  {currentUser?.userType === "super_admin" && (
                    <Box sx={{ 
                      pt: 1,
                      borderTop: `1px solid ${customColors.border.light}`
                    }}>
                      <Typography variant="caption" sx={{ 
                        ...commonStyles.textSecondary,
                        fontSize: '0.7rem'
                      }}>
                        Klijent: {(() => {
                          const userTenantId = typeof user.tenantId === 'object' ? user.tenantId._id : user.tenantId;
                          const tenant = tenants.find(t => t._id === userTenantId);
                          return tenant ? tenant.name : "N/A";
                        })()}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          // Desktop prikaz - tabela
          <div>
            {/* Filteri za pretragu (Ime, Email, Telefon) */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                label="Ime"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                sx={{ minWidth: 200 }}
                size="small"
              />
              <TextField
                label="Email"
                value={filterEmail}
                onChange={(e) => setFilterEmail(e.target.value)}
                sx={{ minWidth: 220 }}
                size="small"
              />
              <TextField
                label="Telefon"
                value={filterPhone}
                onChange={(e) => setFilterPhone(e.target.value)}
                sx={{ minWidth: 160 }}
                size="small"
              />
              <Button
                onClick={handleSearchClick}
                variant="contained"
                startIcon={<SearchIcon />}
                sx={{
                  ...commonStyles.gradientButton,
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  transition: "all 0.3s ease",
                  height: 40,
                  '&:hover': {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 20px ${customColors.primary.main}50`,
                  },
                }}
              >
                Pretraži
              </Button>
              <Button
                onClick={handleClearFilters}
                variant="outlined"
                startIcon={<AutorenewIcon />}
                sx={{
                  ...commonStyles.cancelButton,
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  transition: "all 0.3s ease",
                  height: 40,
                  '&:hover': {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Očisti
              </Button>
            </Box>
            {/* Add User Button za desktop */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={commonStyles.gradientButton}
                disabled={currentUser?.userType === "super_admin" && !selectedTenantId && !showSuperAdmins}
              >
                {isEmployeesPage ? "Dodaj zaposlenog" : "Dodaj korisnika"}
              </Button>
            </Box>
            <TableContainer sx={{ backgroundColor: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: colors.table.header.background,
                  '& .MuiTableCell-head': {
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                    fontWeight: 600,
                    borderBottom: `1px solid ${customColors.border.light}`
                  }
                }}>
                  <TableCell>Ime</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefon</TableCell>
                  {currentUser?.userType === "super_admin" && (
                    <TableCell>Klijent</TableCell>
                  )}
                  {isEmployeesPage && (
                    <TableCell>Tip</TableCell>
                  )}
                  <TableCell>Status</TableCell>
                  {currentTenant?.settings?.partiallyRequireConfirmation && (
                    <TableCell>Ne zahtevaj potvrdu termina</TableCell>
                  )}
                  <TableCell>Akcije</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow key={user._id} sx={{
                    backgroundColor: getActualTableRowBackground(index, isDarkMode),
                    '& .MuiTableCell-body': {
                      borderBottom: `1px solid ${customColors.border.light}`,
                      color: commonStyles.textPrimary.color
                    }
                  }}>
                    <TableCell sx={commonStyles.textPrimary}>
                      <Box display="flex" alignItems="center">
                        <PersonIcon sx={{ mr: 1, color: customColors.primary.main }} />
                        {`${user.firstName} ${user.lastName}`}
                      </Box>
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>{user.email}</TableCell>
                    <TableCell sx={commonStyles.textPrimary}>{user.phone}</TableCell>
                    {currentUser?.userType === "super_admin" && (
                      <TableCell sx={commonStyles.textPrimary}>
                        {(() => {
                          const userTenantId = typeof user.tenantId === 'object' ? user.tenantId._id : user.tenantId;
                          const tenant = tenants.find(t => t._id === userTenantId);
                          return tenant ? tenant.name : "N/A";
                        })()}
                      </TableCell>
                    )}
                    {isEmployeesPage && (
                      <TableCell sx={commonStyles.textPrimary}>
                        {(() => {
                          const tenantIdToUse = selectedTenantId || getCurrentTenantId();
                          const userActualType = getUserTypeFromConnections(user, tenantIdToUse);
                          const finalUserType = userActualType || user.userType;
                          return finalUserType === "tenant_admin" ? "Admin" : "Zaposleni";
                        })()}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={user.isActive ? "Aktivan" : "Neaktivan"}
                        size="small"
                        color={user.isActive ? "success" : "default"}
                        sx={{
                          background: user.isActive ? 'rgba(34, 197, 94, 0.2)' : 'rgba(156, 163, 175, 0.2)',
                          border: user.isActive ? '1px solid rgba(34, 197, 94, 0.5)' : '1px solid rgba(156, 163, 175, 0.5)',
                          color: user.isActive ? '#bbf7d0' : '#d1d5db',
                        }}
                      />
                    </TableCell>
                    {currentTenant?.settings?.partiallyRequireConfirmation && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ ...commonStyles.textSecondary, fontSize: '0.8rem' }}>
                            Ne
                          </Typography>
                          <Switch
                            checked={user.dontRequireConfirmation || false}
                            onChange={() => handleToggleDontRequireConfirmation(user._id, user.dontRequireConfirmation)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: customColors.primary.main,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: customColors.primary.main,
                              },
                            }}
                          />
                          <Typography variant="body2" sx={{ ...commonStyles.textSecondary, fontSize: '0.8rem' }}>
                            Da
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(user)}
                        sx={commonStyles.iconButton(customColors.primary.main)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(user._id)}
                        sx={commonStyles.iconButton(colors.actions.delete)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Redova po stranici:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} od ${count !== -1 ? count : `više od ${to}`}`
            }
          />
          </div>
        )}
      </div>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: commonStyles.dialogPaper
        }}
      >
               <DialogTitle sx={commonStyles.dialogTitle}>
         {editingUser ? (isEmployeesPage ? "Uredi zaposlenog" : "Uredi korisnika") : (isEmployeesPage ? "Dodaj novog zaposlenog" : "Dodaj novog korisnika")}
       </DialogTitle>
        <DialogContent sx={{ ...commonStyles.dialogContent, pt: 2 }}>
          <Box sx={{ pt: 2 }}>
            {(() => {
              // Provera da li je korisnik kreiran od strane drugog korisnika
              // Super admin uvek može da uređuje korisnike, bez obzira na createdBy
              const isSuperAdmin = currentUser?.userType === 'super_admin';
              const isCreatedByOtherUser = !isSuperAdmin && 
                editingUser && 
                editingUser.createdBy && 
                currentUser?._id && 
                editingUser.createdBy !== currentUser._id && 
                formData.userType !== 'tenant_admin'
                ;
              return (
                <>
            {/* Ime */}
            <TextField
              fullWidth
              label="Ime"
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
              disabled={isCreatedByOtherUser}
              sx={{ ...commonStyles.formField, mb: 2 }}
            />

            {/* Prezime */}
            <TextField
              fullWidth
              label="Prezime"
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
              disabled={isCreatedByOtherUser}
              sx={{ ...commonStyles.formField, mb: 2 }}
            />

            {/* Email */}
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              disabled={isCreatedByOtherUser}
              sx={{ ...commonStyles.formField, mb: 2 }}
            />

            {/* Telefon */}
            <TextField
              fullWidth
              label="Telefon"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              disabled={isCreatedByOtherUser}
              sx={{ ...commonStyles.formField, mb: 2 }}
            />

            {/* Lozinka - prikaži samo ako nije kreiran od strane drugog korisnika */}
            {!isCreatedByOtherUser && (
            <TextField
              fullWidth
              label="Lozinka"
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              helperText={
                editingUser
                  ? "Ostavite prazno da zadržite trenutnu lozinku"
                  : "Unesite lozinku za novog korisnika"
              }
              sx={{ ...commonStyles.formField, mb: 2 }}
            />
            )}

            {/* Tip korisnika - sakrij ako je samo jedna opcija i nije employees page */}
            {!isEmployeesPage && userTypes.length === 1 ? null : (
              <FormControl fullWidth sx={{ mb: 2 }} required>
              <InputLabel sx={commonStyles.textSecondary}>Tip korisnika</InputLabel>
              <Select
                  value={formData.userType || (isEmployeesPage ? "employee" : "customer") || ""}
                label="Tip korisnika"
                onChange={(e) =>
                  setFormData({ ...formData, userType: e.target.value })
                }
                disabled={showSuperAdmins || isCreatedByOtherUser}
                sx={commonStyles.selectField}
                  required
              >
                {userTypes.map((type) => {
                  let displayText = type.replace("_", " ").toUpperCase();
                  if (type === "employee") {
                    displayText = "Zaposleni";
                  } else if (type === "tenant_admin") {
                    displayText = "Admin";
                  }
                  return (
                    <MenuItem key={type} value={type}>
                      {displayText}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            )}

            {/* Klijent (samo za super_admin) */}
            {currentUser?.userType === "super_admin" && !showSuperAdmins && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={commonStyles.textSecondary}>Klijent</InputLabel>
                <Select
                  value={formData.tenantId}
                  label="Klijent"
                  onChange={(e) =>
                    setFormData({ ...formData, tenantId: e.target.value })
                  }
                  sx={commonStyles.selectField}
                  disabled={true || isCreatedByOtherUser}
                >
                  <MenuItem value={formData.tenantId}>
                    {(() => {
                      const tenant = tenants.find(t => t._id === formData.tenantId);
                      return tenant ? tenant.name : "N/A";
                    })()}
                  </MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Radno vreme - samo za employee i tenant_admin */}
            {(formData.userType === "employee" || formData.userType === "tenant_admin") && (
              <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2, ...commonStyles.textPrimary }}>
                  Radno vreme
            </Typography>

                {/* Ako tenant ima smene, prikaži select za smene */}
                {currentTenant?.settings?.hasShifts && currentTenant?.shifts?.length > 0 ? (
                  <>
            <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel sx={commonStyles.textSecondary}>Smena</InputLabel>
              <Select
                        value={formData.shift}
                        label="Smena"
                        onChange={(e) => {
                          const selectedShiftName = e.target.value;
                          const selectedShift = currentTenant.shifts.find(s => s.name === selectedShiftName);
                          
                          if (selectedShift) {
                  setFormData({
                    ...formData,
                              shift: selectedShiftName,
                              workingHours: {
                                startTime: selectedShift.timeFrom,
                                endTime: selectedShift.timeTo
                              }
                            });
                          } else {
                            // Ako je "Ručno", ostavi trenutna vremena ili postavi default
                            setFormData({
                              ...formData,
                              shift: "",
                              workingHours: formData.workingHours.startTime ? formData.workingHours : getDefaultWorkingHours()
                            });
                          }
                        }}
                        disabled={isCreatedByOtherUser}
                sx={commonStyles.selectField}
              >
                        <MenuItem value="">
                          <em>Ručno unos vremena</em>
                        </MenuItem>
                        {currentTenant.shifts.map((shift) => (
                          <MenuItem key={shift.name} value={shift.name}>
                            {shift.name} ({shift.timeFrom} - {shift.timeTo})
                          </MenuItem>
                        ))}
              </Select>
            </FormControl>
                  </>
                ) : null}

                {/* Polja za radno vreme */}
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <FormControl fullWidth>
                    <InputLabel sx={commonStyles.textSecondary}>Radno vreme od</InputLabel>
                    <Select
                      value={formData.workingHours.startTime}
                      label="Radno vreme od"
                      onChange={(e) => setFormData({
                        ...formData,
                        workingHours: {
                          ...formData.workingHours,
                          startTime: e.target.value
                        },
                        shift: "" // Resetuj smenu ako ručno menja vreme
                      })}
                      sx={commonStyles.selectField}
                      disabled={(!!formData.shift && currentTenant?.settings?.hasShifts) || isCreatedByOtherUser}
                    >
                      {[
                        '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30',
                        '04:00', '04:30', '05:00', '05:30', '06:00', '06:30', '07:00', '07:30',
                        '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
                        '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
                        '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
                        '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00', '23:30'
                      ].map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel sx={commonStyles.textSecondary}>Radno vreme do</InputLabel>
                    <Select
                      value={formData.workingHours.endTime}
                      label="Radno vreme do"
                      onChange={(e) => setFormData({
                        ...formData,
                        workingHours: {
                          ...formData.workingHours,
                          endTime: e.target.value
                        },
                        shift: "" // Resetuj smenu ako ručno menja vreme
                      })}
                      sx={commonStyles.selectField}
                      disabled={(!!formData.shift && currentTenant?.settings?.hasShifts) || isCreatedByOtherUser}
                    >
                      {[
                        '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30',
                        '04:00', '04:30', '05:00', '05:30', '06:00', '06:30', '07:00', '07:30',
                        '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
                        '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
                        '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
                        '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00', '23:30'
                      ].map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {formData.shift && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Izabrana smena: <strong>{formData.shift}</strong> ({formData.workingHours.startTime} - {formData.workingHours.endTime})
                  </Alert>
                )}
              </Box>
            )}

            {/* Dozvole - samo za korisnike koji nisu customer */}
            {formData.userType !== "customer" && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, ...commonStyles.textPrimary }}>
                  Dozvole
                </Typography>
                {/* Može da upravlja korisnicima */}
            <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel sx={commonStyles.textSecondary}>Može da upravlja korisnicima</InputLabel>
              <Select
                    value={formData.permissions.canManageUsers}
                    label="Može da upravlja korisnicima"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    permissions: {
                      ...formData.permissions,
                          canManageUsers: e.target.value,
                    },
                  })
                }
                sx={commonStyles.selectField}
                disabled={isCreatedByOtherUser}
              >
                <MenuItem value={true}>Da</MenuItem>
                <MenuItem value={false}>Ne</MenuItem>
              </Select>
            </FormControl>


            {/* Može da upravlja uslugama */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={commonStyles.textSecondary}>Može da upravlja uslugama</InputLabel>
              <Select
                value={formData.permissions.canManageServices}
                label="Može da upravlja uslugama"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    permissions: {
                      ...formData.permissions,
                      canManageServices: e.target.value,
                    },
                  })
                }
                sx={commonStyles.selectField}
                disabled={isCreatedByOtherUser}
              >
                <MenuItem value={true}>Da</MenuItem>
                <MenuItem value={false}>Ne</MenuItem>
              </Select>
            </FormControl>

            {/* Može da vidi izveštaje */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={commonStyles.textSecondary}>Može da vidi izveštaje</InputLabel>
              <Select
                value={formData.permissions.canViewReports}
                label="Može da vidi izveštaje"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    permissions: {
                      ...formData.permissions,
                      canViewReports: e.target.value,
                    },
                  })
                }
                sx={commonStyles.selectField}
                disabled={isCreatedByOtherUser}
              >
                <MenuItem value={true}>Da</MenuItem>
                <MenuItem value={false}>Ne</MenuItem>
              </Select>
            </FormControl>
              </>
            )}
            {/* Status */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={commonStyles.textSecondary}>Status</InputLabel>
              <Select
                value={formData.isActive}
                label="Status"
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.value })
                }
                sx={commonStyles.selectField}
              >
                <MenuItem value={true}>Aktivan</MenuItem>
                <MenuItem value={false}>Neaktivan</MenuItem>
              </Select>
            </FormControl>

            {/* Bez potvrde termina - prikaži samo ako tenant ima partiallyRequireConfirmation */}
            {currentTenant?.settings?.partiallyRequireConfirmation && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={commonStyles.textSecondary}>Bez potvrde termina</InputLabel>
                <Select
                  value={formData.dontRequireConfirmation}
                  label="Bez potvrde termina"
                  onChange={(e) =>
                    setFormData({ ...formData, dontRequireConfirmation: e.target.value })
                  }
                  sx={commonStyles.selectField}
                  disabled={isCreatedByOtherUser}
                >
                  <MenuItem value={false}>Ne - potrebna potvrda</MenuItem>
                  <MenuItem value={true}>Da - bez potvrde</MenuItem>
                </Select>
              </FormControl>
            )}
                </>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions sx={commonStyles.dialogActions}>
          <Button onClick={handleCloseDialog} variant="outlined" sx={commonStyles.cancelButton}>
            Otkaži
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            sx={commonStyles.gradientButton}
            disabled={submitting}
          >
            {editingUser ? "Ažuriraj" : "Kreiraj"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobilni filter drawer */}
      <Drawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={handleCloseMobileFilter}
        PaperProps={{
          sx: {
            ...commonStyles.paper,
            borderRadius: '16px 16px 0 0',
            maxHeight: '80vh',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, ...commonStyles.textPrimary }}>
            Filteri
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Ime"
              value={tempFilters.filterName}
              onChange={(e) => setTempFilters(p => ({ ...p, filterName: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Email"
              value={tempFilters.filterEmail}
              onChange={(e) => setTempFilters(p => ({ ...p, filterEmail: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label="Telefon"
              value={tempFilters.filterPhone}
              onChange={(e) => setTempFilters(p => ({ ...p, filterPhone: e.target.value }))}
              fullWidth
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Button
              onClick={handleResetMobileFilters}
              variant="outlined"
              sx={{ ...commonStyles.cancelButton, flex: 1 }}
            >
              Resetuj
            </Button>
            <Button
              onClick={handleApplyMobileFilters}
              variant="contained"
              sx={{ ...commonStyles.gradientButton, flex: 1 }}
              startIcon={<CheckIcon />}
            >
              Primeni
            </Button>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default Users;
